<template>
	<!-- 注册弹窗 -->
	<el-dialog v-model="dialogVisible" :title="$t('uChoicePc.registerTitle')" width="648px" top="10vh" :close-on-click-modal="false" class="register-dialog">
		<el-form ref="registerFormRef" :model="registerForm" label-position="top" label-width="auto" :rules="formRules" size="large" class="mt-[16px] px-[24px]">
			<!-- 联系电话 -->
			<el-form-item prop="phone">
				<template #label>
					<span>{{ $t('uChoicePc.phoneLabel') }}</span>
				</template>
				<div class="flex flex-1 items-center gap-2">
					<el-select v-model="registerForm.phoneCountryCode" class="w-20">
						<el-option v-for="item in countryCodes" :key="item.code" :label="item.code" :value="item.code" />
					</el-select>
					<el-input v-model="registerForm.phone" :placeholder="$t('uChoicePc.placeholderInput')" class="flex-1 w-[100%]" />
				</div>
			</el-form-item>

			<!-- 邮箱 -->
			<el-form-item prop="email">
				<template #label>
					<span>{{ $t('uChoicePc.emailLabel') }}</span>
				</template>
				<el-input v-model="registerForm.email" :placeholder="$t('uChoicePc.placeholderInput')" />
			</el-form-item>

			<!-- 验证码 -->
			<el-form-item prop="verificationCode">
				<template #label>
					<span>{{ $t('uChoicePc.codeLabel') }}</span>
				</template>
				<div class="flex items-center flex-1">
					<el-input v-model="registerForm.verificationCode" :placeholder="$t('uChoicePc.placeholderInput')" class="flex-1">
						<template #suffix>
							<el-button class="bg-transparent hover:bg-transparent" text :disabled="countdown > 0" @click="handleGetCode">
								<span :class="[countdown === 0 ? 'text-[#F92863]' : '']">
									{{ countdown > 0 ? `${countdown}${$t('uChoicePc.countdownText')}` : $t('uChoicePc.getCode') }}</span
								>
							</el-button>
						</template>
					</el-input>
				</div>
			</el-form-item>

			<!-- 设置密码 -->
			<el-form-item prop="password">
				<template #label>
					<span>{{ $t('uChoicePc.pwdLabel') }}</span>
				</template>
				<el-input v-model="registerForm.password" show-password :placeholder="$t('uChoicePc.placeholderInput')" />
			</el-form-item>

			<!-- 再次确认密码 -->
			<el-form-item prop="confirmPassword">
				<template #label>
					<span>{{ $t('uChoicePc.confirmPwdLabel') }}</span>
				</template>
				<el-input v-model="registerForm.confirmPassword" show-password :placeholder="$t('uChoicePc.placeholderInput')" />
			</el-form-item>
		</el-form>

		<!-- 底部按钮 -->
		<template #footer>
			<div class="flex justify-center gap-6">
				<el-button class="w-[160px] h-[54px] rounded-[16px] bg-[#F4F4F4] border-none hover:bg-gray-200" @click="dialogVisible = false">
					<span class="text-[18px]">{{ $t('uChoicePc.cancelBtn') }}</span>
				</el-button>
				<el-button type="danger" class="w-[160px] h-[54px] rounded-[10px]" @click="handleRegister">
					<span class="w-[160px] text-[18px] text-white">{{ $t('uChoicePc.registerBtn') }}</span>
				</el-button>
			</div>
		</template>
	</el-dialog>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import lang from '@/lang/index'
import { getEmilCode, registerSave, getSelectorNoAuth } from '@/api/mall/index'
const i18n = lang.global

// 弹窗显示状态
const dialogVisible = ref(false)
const open = () => {
	dialogVisible.value = true
	getSysType()
}
// 表单DOM引用
const registerFormRef = ref<FormInstance | null>(null)
// 验证码倒计时
const countdown = ref(0)
let timer: number | null = null

// 表单类型定义
interface RegisterForm {
	phoneCountryCode: string
	phone: string
	email: string
	verificationCode: string
	password: string
	confirmPassword: string
	registerType: number
}
interface Params {
	[key: string]: any
}
// 表单绑定数据
const registerForm = ref<RegisterForm>({
	phoneCountryCode: '+86',
	phone: '',
	email: '',
	verificationCode: '',
	password: '',
	confirmPassword: '',
	registerType: 1
})

// 表单校验规则
const formRules = reactive<FormRules>({
	phone: [
		{ required: true, message: i18n.t('uChoicePc.phoneRuleTip'), trigger: 'blur' },
		{ pattern: /^\d{6,15}$/, message: i18n.t('uChoicePc.phoneRuleTip'), trigger: 'blur' }
	],
	email: [
		{ required: true, message: i18n.t('uChoicePc.emailRuleTip'), trigger: 'blur' },
		{ type: 'email', message: i18n.t('uChoicePc.emailRuleTip'), trigger: 'blur' }
	],
	verificationCode: [{ required: true, message: i18n.t('uChoicePc.codeRuleTip'), trigger: 'blur' }],
	password: [
		{ required: true, message: i18n.t('uChoicePc.pwdRuleTip'), trigger: 'blur' },
		{ min: 6, message: i18n.t('uChoicePc.pwdRuleTip'), trigger: 'blur' }
	],
	confirmPassword: [
		{ required: true, message: i18n.t('uChoicePc.pwdRuleTip'), trigger: 'blur' },
		{
			validator: (rule: any, value: string, callback: Function) => {
				if (value !== registerForm.value.password) {
					callback(new Error(i18n.t('uChoicePc.pwdDiffTip')))
				} else {
					callback()
				}
			},
			trigger: 'blur'
		}
	]
})

const countryCodes = ref<Params[]>([])
// 获取国家编码
const getSysType = () => {
	const params: any = {
		type: 'DICT_DATA',
		business: 'countryCode'
	}
	getSelectorNoAuth(params).then((res: any) => {
		countryCodes.value = res.data || []
	})
}

// 获取验证码
const handleGetCode = () => {
	// 先校验邮箱
	registerFormRef.value?.validateField('email', (valid) => {
		if (!valid) return
		const params: any = {
			email: registerForm.value.email
		}
		getEmilCode(params)
			.then(() => {
				ElMessage.success(i18n.t('uChoicePc.codeSendSuccess'))
				// 开启60秒倒计时
				countdown.value = 60
				timer = window.setInterval(() => {
					countdown.value--
					if (countdown.value <= 0) {
						clearInterval(timer!)
					}
				}, 1000)
			})
			.catch(() => {
				// 请求失败静默处理
			})
	})
}

// 提交注册
const handleRegister = async () => {
	if (!registerFormRef.value) return
	await registerFormRef.value.validate((valid) => {
		if (valid) {
			// 此处对接后端注册接口
			registerSave(registerForm.value)
				.then((res: any) => {
					if (res.code === 200) {
						ElMessage.success(i18n.t('uChoicePc.registerSuccess'))
						// 关闭弹窗、重置表单
						dialogVisible.value = false
						registerFormRef.value?.resetFields()
						countdown.value = 0
						if (timer) clearInterval(timer)
					}
				})
				.catch(() => {})
		}
	})
}
defineExpose({
	open
})
onBeforeUnmount(() => {
	if (timer) clearInterval(timer)
})
</script>

<style scoped>
/* 仅少量深度覆盖，其余全部UnoCSS */
.register-dialog {
	:deep(.el-dialog__header) {
		padding-bottom: 10px;
	}
	:deep(.el-form-item__label) {
		font-size: 16px;
	}
	:deep(.el-input__inner) {
		height: 48px;
	}
	:deep(.el-form-item--large) {
		margin-bottom: 32px;
	}
	:deep(.el-dialog .el-dialog__footer) {
		padding: 0 24px 24px !important;
	}
}
</style>
